import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { OpeningTimes } from 'apis/restaurant-api/merchant-admin/venue/types';
import { Typography as T } from 'common/typography';
import Loading from 'core/Loading';

import { transformDeliveryTimesToBreakdownByWeekday } from '../utils';

type Props = {
  className?: string;
  header: string;
  openingTimes: OpeningTimes | null;
  deliveryProviderDeliveryTimes: OpeningTimes | null;
  deliveryTimes: OpeningTimes | null;
  cappedDeliveryTimes: OpeningTimes | null;
  isSelfDelivery?: boolean;
  isLoading?: boolean;
};

const weekdays: { [day: string]: string } = {
  monday: 'week.mon',
  tuesday: 'week.tue',
  wednesday: 'week.wed',
  thursday: 'week.thu',
  friday: 'week.fri',
  saturday: 'week.sat',
  sunday: 'week.sun',
};

const Root = styled.div``;

const Header = styled(T.Heading6)`
  color: ${cssThemedValue('textPrimary')};
  display: flex;
  justify-content: space-between;
  margin-block-end: 24px;
`;

const DayField = styled(T.Body3)`
  width: 100%;
  margin-block-end: 12px;
  display: flex;
  justify-content: space-between;
  color: ${cssThemedValue('textPrimary')};
  text-align: end;
  text-transform: capitalize;
`;

const InfoContainer = styled.div`
  display: flex;
`;

const Times = styled(T.Body3)`
  display: flex;
  flex-flow: column;
  text-align: end;
  margin-inline-start: 8px;
`;

const Tooltip = styled.div``;

const Content = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const Section = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  &:not(:last-of-type) {
    margin-block-end: 12px;
  }
`;

const SectionHeading = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
`;

const SectionContent = styled(T.Body3)<{ highlight?: boolean }>`
  display: flex;
  font-weight: ${({ highlight }) => (highlight ? 'bold' : 'normal')};
`;

const InfoIcon = styled(InfoCircleFilled).attrs({ height: 12 })`
  fill: ${cssThemedValue('textSecondary')};
  margin-inline-start: 8px;
`;

const DeliveryTimes = ({
  className,
  header,
  openingTimes,
  deliveryProviderDeliveryTimes,
  deliveryTimes,
  cappedDeliveryTimes,
  isSelfDelivery,
  isLoading,
}: Props) => {
  const deliveryTimesBreakdowns = useMemo(
    () =>
      transformDeliveryTimesToBreakdownByWeekday({
        openingTimes,
        deliveryProviderDeliveryTimes,
        deliveryTimes,
        cappedDeliveryTimes,
        isSelfDelivery,
      }),
    [openingTimes, deliveryProviderDeliveryTimes, deliveryTimes, cappedDeliveryTimes, isSelfDelivery],
  );

  return (
    <Root className={className}>
      <Header>
        <FormattedMessage id={header} />
      </Header>
      {isLoading ? (
        <Loading />
      ) : (
        deliveryTimesBreakdowns.map(
          ({
            weekday,
            openingTimesText,
            hideDeliveryProviderDeliveryTimes,
            deliveryProviderDeliveryTimesText,
            defaultDeliveryTimes,
            deliveryTimesText,
            cappedDeliveryTimesText,
          }) => (
            <DayField key={weekday}>
              <T.Body3>
                <FormattedMessage id={weekdays[weekday]} />
              </T.Body3>
              <InfoContainer>
                <Times>
                  {cappedDeliveryTimesText.map((text, i) => (
                    <T.Body3 key={i}>{text}</T.Body3>
                  ))}
                  {!cappedDeliveryTimesText.length && <FormattedMessage id="opening-hours.closed" />}
                </Times>
                <Tooltip>
                  <Popover
                    placement="left"
                    renderArrow
                    content={
                      <Content>
                        <Section>
                          <SectionHeading>
                            <FormattedMessage id="venue-details.opening-times" />
                          </SectionHeading>
                          <SectionContent>
                            <FormattedMessage id={weekdays[weekday]} />
                            <Times>
                              {openingTimesText.map((text, i) => (
                                <T.Body3 key={i}>{text}</T.Body3>
                              ))}
                              {!openingTimesText.length && <FormattedMessage id="opening-hours.closed" />}
                            </Times>
                          </SectionContent>
                        </Section>
                        {!hideDeliveryProviderDeliveryTimes && (
                          <Section>
                            <SectionHeading>
                              <FormattedMessage id="venue-details.wolt-delivery-times" />
                            </SectionHeading>
                            <SectionContent>
                              {deliveryProviderDeliveryTimesText ? (
                                <>
                                  <FormattedMessage id={weekdays[weekday]} />
                                  <Times>
                                    {deliveryProviderDeliveryTimesText.map((text, i) => (
                                      <T.Body3 key={i}>{text}</T.Body3>
                                    ))}
                                    {!deliveryProviderDeliveryTimesText.length && (
                                      <FormattedMessage id="opening-hours.closed" />
                                    )}
                                  </Times>
                                </>
                              ) : (
                                '-'
                              )}
                            </SectionContent>
                          </Section>
                        )}
                        <Section>
                          <SectionHeading>
                            <FormattedMessage id="venue-details.delivery-times" />
                          </SectionHeading>
                          <SectionContent>
                            {deliveryTimesText ? (
                              <>
                                <FormattedMessage id={weekdays[weekday]} />
                                <Times>
                                  {deliveryTimesText.map((text, i) => (
                                    <T.Body3 key={i}>{text}</T.Body3>
                                  ))}
                                  {!deliveryTimesText.length && <FormattedMessage id="opening-hours.closed" />}
                                </Times>
                              </>
                            ) : (
                              <>
                                {defaultDeliveryTimes === 'wolt-delivery-times' ? (
                                  <FormattedMessage id="venue-details.using-wolt-delivery-hours" />
                                ) : (
                                  '-'
                                )}
                              </>
                            )}
                          </SectionContent>
                        </Section>
                        <Section>
                          <SectionHeading>
                            <FormattedMessage id="venue-details.effective-delivery-times" />
                          </SectionHeading>
                          <SectionContent highlight>
                            <FormattedMessage id={weekdays[weekday]} />
                            <Times>
                              {cappedDeliveryTimesText.map((text, i) => (
                                <T.Body3 key={i}>{text}</T.Body3>
                              ))}
                              {!cappedDeliveryTimesText.length && <FormattedMessage id="opening-hours.closed" />}
                            </Times>
                          </SectionContent>
                        </Section>
                      </Content>
                    }
                  >
                    <InfoIcon />
                  </Popover>
                </Tooltip>
              </InfoContainer>
            </DayField>
          ),
        )
      )}
    </Root>
  );
};

export default DeliveryTimes;
